import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStore, faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  fetchMarkets,
  fetchSlots,
  setSelectedStore,
  setPickupDate,
  setSelectedSlot,
} from "../../../redux/checkout/action";

const DAY_NAMES = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

function formatDateLabel(date) {
  const dayName = DAY_NAMES[date.getDay()];
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return dayName + " " + day + "/" + month;
}

function formatDateValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + d;
}

function formatTime(t) {
  if (!t) return "";
  return t.substring(0, 5); // "09:00:00" → "09:00"
}

function formatSlotLabel(slot) {
  if (slot.time_from && slot.time_to) {
    return formatTime(slot.time_from) + " - " + formatTime(slot.time_to);
  }
  if (slot.start && slot.end) return slot.start + " - " + slot.end;
  if (slot.time) return slot.time;
  if (slot.label) return slot.label;
  return "Slot";
}

function PickupForm(props) {
  const {
    markets,
    selectedStore,
    pickupDate,
    availableSlots,
    selectedSlot,
  } = props.checkout;
  // Errori calcolati da ShippingStep (unica validazione dello step 2).
  const errors = props.errors || {};

  const [search, setSearch] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (!markets || markets.length === 0) {
      props.fetchMarkets();
    }
  }, []);

  const pickupStores = (markets || []).filter(
    (m) => m.pickup === 1 || m.pickup === true
  );

  const filteredStores = search.trim()
    ? pickupStores.filter((s) => {
        const q = search.toLowerCase();
        const name = (s.name || s.nome || "").toLowerCase();
        const addr = (s.address || s.indirizzo || "").toLowerCase();
        const city = (s.city?.name || "").toLowerCase();
        return name.includes(q) || addr.includes(q) || city.includes(q);
      })
    : pickupStores;

  // Un solo negozio abilitato al ritiro: selezionalo da solo. Solo se non ce n'e'
  // gia' uno, perche' setSelectedStore azzera data e orario (es. ritorno dallo step 3).
  useEffect(() => {
    if (pickupStores.length === 1 && !selectedStore) {
      props.setSelectedStore(pickupStores[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupStores.length, selectedStore]);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date();
  dayAfter.setDate(dayAfter.getDate() + 2);
  const dayAfter2 = new Date();
  dayAfter2.setDate(dayAfter2.getDate() + 3);

  const dateOptions = [
    { label: formatDateLabel(tomorrow), value: formatDateValue(tomorrow) },
    { label: formatDateLabel(dayAfter), value: formatDateValue(dayAfter) },
    { label: formatDateLabel(dayAfter2), value: formatDateValue(dayAfter2) },
  ];

  const handleSelectStore = (store) => {
    props.setSelectedStore(store);
  };

  const handleSelectDate = async (dateValue) => {
    props.setPickupDate(dateValue);
    if (selectedStore) {
      setLoadingSlots(true);
      await props.fetchSlots(selectedStore.unique_id || selectedStore.id, dateValue);
      setLoadingSlots(false);
    }
  };

  const isSlotEnabled = (slot) => {
    if (slot.enabled !== undefined) return slot.enabled === 1 || slot.enabled === true;
    if (slot.available !== undefined) return slot.available !== false;
    return true;
  };

  const handleSelectSlot = (slot) => {
    if (!isSlotEnabled(slot)) return;
    props.setSelectedSlot(slot);
  };

  const singleStore = pickupStores.length === 1;

  const isSelected = (store) =>
    !!selectedStore &&
    (selectedStore.id || selectedStore.unique_id) === (store.id || store.unique_id);

  const renderStore = (store, opts = {}) => (
    <div
      key={store.id || store.unique_id}
      className={
        "co-store" +
        (isSelected(store) ? " co-store--selected" : "") +
        (opts.static ? " co-store--static" : "")
      }
      onClick={opts.static ? undefined : () => handleSelectStore(store)}
    >
      <FontAwesomeIcon icon={faStore} style={{ color: "#888", flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <p className="co-store__name">{store.name || store.nome}</p>
        <p className="co-store__address">
          {store.address || store.indirizzo}
          {store.city?.name ? `, ${store.city.name}` : ""}
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 14 }}>
      {/* Store selector with search */}
      <div
        className={"co-form__group" + (errors.selectedStore ? " co-form__group--error" : "")}
        data-co-field="selectedStore"
      >
        <label className="co-form__label">
          {singleStore ? "Negozio di ritiro" : "Seleziona negozio"}
        </label>

        {singleStore ? (
          // Un solo negozio: gia' selezionato, niente ricerca ne' lista.
          renderStore(pickupStores[0], { static: true })
        ) : (
          <>
            <div style={{ position: "relative", marginBottom: 10 }}>
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                  color: "#aaa", fontSize: "0.85rem",
                }}
              />
              <input
                className="co-form__input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca negozio per nome o indirizzo..."
                style={{ paddingLeft: 36 }}
              />
            </div>

            <div style={{
              maxHeight: "220px",
              overflowY: "auto",
              border: pickupStores.length > 3 ? "1px solid #f0f0f0" : "none",
              borderRadius: 12,
            }}>
              {filteredStores.length === 0 && (
                <p style={{ fontSize: "0.88rem", color: "#999", padding: "12px 14px" }}>
                  {pickupStores.length === 0
                    ? "Nessun negozio disponibile per il ritiro."
                    : "Nessun risultato per la ricerca."}
                </p>
              )}
              {filteredStores.map((store) => renderStore(store))}
            </div>
          </>
        )}

        {errors.selectedStore && (
          <span className="co-form__error">{errors.selectedStore}</span>
        )}
      </div>

      {/* Date picker */}
      {selectedStore && (
        <div
          className={"co-form__group" + (errors.pickupDate ? " co-form__group--error" : "")}
          data-co-field="pickupDate"
        >
          <label className="co-form__label">Seleziona giorno</label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {dateOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={
                  "co-slot__item" +
                  (pickupDate === opt.value ? " co-slot__item--selected" : "")
                }
                onClick={() => handleSelectDate(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {errors.pickupDate && (
            <span className="co-form__error">{errors.pickupDate}</span>
          )}
        </div>
      )}

      {/* Time slot grid */}
      {selectedStore && pickupDate && (
        <div
          className={"co-form__group" + (errors.selectedSlot ? " co-form__group--error" : "")}
          data-co-field="selectedSlot"
        >
          <label className="co-form__label">Seleziona orario</label>
          {loadingSlots ? (
            <div style={{ padding: "10px 0", color: "#888", fontSize: "0.88rem" }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 6 }} />
              Caricamento orari...
            </div>
          ) : availableSlots.length === 0 ? (
            <p style={{ fontSize: "0.88rem", color: "#999" }}>
              Nessun orario disponibile per questa data.
            </p>
          ) : (
            <div className="co-slot">
              {availableSlots.map((slot, idx) => {
                const enabled = isSlotEnabled(slot);
                const isSelected = selectedSlot &&
                  selectedSlot.time_from === slot.time_from &&
                  selectedSlot.time_to === slot.time_to;
                return (
                  <button
                    key={idx}
                    type="button"
                    className={
                      "co-slot__item" +
                      (isSelected ? " co-slot__item--selected" : "") +
                      (!enabled ? " co-slot__item--disabled" : "")
                    }
                    disabled={!enabled}
                    onClick={() => handleSelectSlot(slot)}
                  >
                    {formatSlotLabel(slot)}
                  </button>
                );
              })}
            </div>
          )}
          {errors.selectedSlot && (
            <span className="co-form__error">{errors.selectedSlot}</span>
          )}
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  checkout: state.checkout,
});

export default connect(mapStateToProps, {
  fetchMarkets,
  fetchSlots,
  setSelectedStore,
  setPickupDate,
  setSelectedSlot,
})(PickupForm);
